import { getEdgeFunctionHeaders } from 'common/src/supabase';
import type { WholesaleRecord, RetailRecord, UnitMode } from './types';
import { SEASONS } from '../../../../common/src/utils/definitions';

// ============================================================
// CONSTANTS
// ============================================================

export const TERMINAL_REPORTS: Record<string, string> = {
  '2277': 'Atlanta',
  '2281': 'Baltimore',
  '2285': 'Boston',
  '2290': 'Chicago',
  '2302': 'Detroit',
  '2306': 'Los Angeles',
  '2310': 'Miami',
  '2314': 'New York',
  '2318': 'Philadelphia',
};

export const SHIPPING_REPORTS: Record<string, string> = {
  '2390': 'Fresno',
  '2395': 'Miami',
  '2399': 'Orlando',
  '2401': 'Orlando Imports',
  '2402': 'Phoenix',
  '2404': 'Raleigh',
  '2409': 'Thomasville',
  '2412': 'Yakima',
};

export const RETAIL_SLUG = '3324';

// Active district "TRUCK, AIR, AND BOAT Daily Movement" reports — shipment
// volume at origin. Summed across districts they approximate national
// industry movement for a commodity (verified against the live report
// catalog; discontinued cities excluded).
export const MOVEMENT_REPORTS: Record<string, string> = {
  '2718': 'Atlanta',
  '2768': 'Benton Harbor',
  '2793': 'Columbia',
  '3119': 'El Centro',
  '2899': 'Fresno',
  '2925': 'Idaho Falls',
  '2762': 'Inwood',
  '3123': 'McAllen',
  '3031': 'Miami',
  '3125': 'Nogales',
  '3170': 'Onley',
  '3105': 'Orlando',
  '3127': 'Phoenix',
  '3165': 'Raleigh',
  '2717': 'Thomasville',
  '3283': 'Washington DC',
  '3332': 'Yakima',
};

export const SPECIES_TO_USDA_COMMODITY: Record<string, string> = {
  CHERRY: 'Cherries',
  NECTARINE: 'Nectarines',
  'SUGAR PLUM': 'Plums',
  PLUM: 'Plums',
  GRAPE: 'Grapes',
  APPLE: 'Apples',
  KIWI: 'Kiwifruit',
  PEACH: 'Peaches',
  ORANGE: 'Oranges',
  LEMON: 'Lemons',
  STRAWBERRY: 'Strawberries',
  BLUEBERRY: 'Blueberries',
  MANGO: 'Mangoes',
  AVOCADO: 'Avocados',
  PEAR: 'Pears',
};

export const MARKET_COLORS: Record<string, string> = {
  'New York': '#e11d48',
  'Los Angeles': '#2563eb',
  Chicago: '#16a34a',
  Philadelphia: '#9333ea',
  Atlanta: '#ea580c',
  Boston: '#0891b2',
  Baltimore: '#4f46e5',
  Detroit: '#ca8a04',
  Miami: '#db2777',
  Fresno: '#65a30d',
  'Orlando Imports': '#fb923c',
  Orlando: '#f97316',
  Phoenix: '#a78bfa',
  Raleigh: '#14b8a6',
  Thomasville: '#8b5cf6',
  Yakima: '#6366f1',
  'Benton Harbor': '#f59e0b',
  'Washington DC': '#059669',
};

export const REGION_COLORS: Record<string, string> = {
  National: '#e11d48',
  Northeast: '#2563eb',
  Southeast: '#16a34a',
  Midwest: '#9333ea',
  Southcentral: '#ea580c',
  Southwest: '#0891b2',
  Northwest: '#4f46e5',
  Hawaii: '#ca8a04',
  Alaska: '#db2777',
};

export const RETAIL_UNIT_LBS: Record<string, number> = {
  Cherries: 1.0,
  Nectarines: 0.5,
  Peaches: 0.5,
  Grapes: 1.5,
  Plums: 0.5,
  Apples: 1.0,
  Oranges: 1.0,
  Lemons: 0.5,
  Strawberries: 1.0,
  Blueberries: 0.39,
  Mangoes: 0.75,
  Avocados: 0.35,
  Kiwifruit: 0.25,
  Pears: 0.5,
};

const LB_PER_KG = 2.20462;

// ============================================================
// HELPERS
// ============================================================

export const parseUSDate = (dateStr: string): Date => {
  const [month, day, year] = dateStr.split('/');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

export const fmtDate = (dateStr: string): string => {
  const d = parseUSDate(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const fmtWeek = (beginDate: string, endDate: string): string => {
  return `${fmtDate(beginDate)} – ${fmtDate(endDate)}`;
};

export const shortMarket = (name: string): string =>
  name
    .replace(' Terminal Market', '')
    .replace(' FOB SC', '')
    .replace(' (Imports)', ' Imports')
    .trim()
    .replace(/,\s*\w{2}$/, '');

/**
 * Pesos netos estándar (lb) de formatos USDA que no declaran su peso en el
 * texto. Sin esto, cualquier commodity cuyo empaque no empiece con un número
 * queda sin precio por kilo y desaparece de los gráficos: palta se perdía
 * entera (100% de las filas), manzana y pera ~88%. Cereza y uva nunca lo
 * notaron porque sus formatos sí traen el peso ("18 lb cartons").
 *
 * Las claves se comparan por substring sobre el texto en minúsculas, de la
 * más específica a la más general (el orden importa: "4/5 bushel cartons
 * wrapped" antes que "bushel cartons").
 */
/**
 * Pesos netos estándar (lb) de formatos USDA que no declaran su peso en el
 * texto. Sin esto, cualquier commodity cuyo empaque no empiece con un número
 * queda sin precio por kilo y desaparece de los gráficos: palta se perdía
 * entera (100% de las filas), manzana y pera ~88%. Cereza y uva nunca lo
 * notaron porque sus formatos sí traen el peso ("18 lb cartons").
 *
 * Van POR COMMODITY porque el mismo texto pesa distinto según la fruta: un
 * bushel de manzana ronda las 42 lb y uno de pera las 50, y "4/5 bushel
 * cartons" aparece tanto en palta como en pera. Se busca por substring, de la
 * clave más específica a la más general (el orden importa).
 *
 * Fuentes:
 *  - USDA Market News User Guide, "Container Approximate Net Weights":
 *    arándanos, frutillas, limones, naranjas, kiwi, mango y carozos.
 *  - Palta: USDA Avocado Inspection Service certifica la caja 2-layer en
 *    25 lb ± 0,5 (estándar de industria para Hass).
 *  - Manzana: 40 lb la caja layered/tray pack (WSDA Standard Pack Size);
 *    42 lb el bushel usado como referencia estadística.
 *  - Pera: 44 lb la caja 4/5 bushel, estándar del noroeste — la industria
 *    reporta su producción en "44 lb cartons" (Northwest Horticultural
 *    Council) y el calibre se define por cuántas peras entran en ella;
 *    22 lb la media caja ("western standard half box", 7 CFR 51.1269).
 */
type PackageWeightRule = {
  match: string;
  lbs: number;
  /** The USDA guide assigns a different standard to these origins. */
  origins?: string[];
};

export const PACKAGE_WEIGHTS_BY_COMMODITY: Record<string, PackageWeightRule[]> = {
  Avocados: [
    { match: 'cartons 2 layer', lbs: 25 },
    { match: 'cartons 1 layer', lbs: 12.5 },
    { match: '2 layer', lbs: 25 },
    { match: '1 layer', lbs: 12.5 },
    { match: '4/5 bushel cartons', lbs: 25 },
  ],
  Apples: [
    { match: 'cartons tray pack', lbs: 40 },
    { match: 'large cartons soft cell pack', lbs: 40 },
    { match: 'cartons soft cell pack', lbs: 40 },
    { match: 'cartons cell pack', lbs: 40 },
    { match: '1/2 bushel cartons', lbs: 21 },
    { match: 'bushel cartons', lbs: 42 },
    { match: 'bushel', lbs: 42 },
  ],
  Pears: [
    // La caja 4/5 bushel de 44 lb ES el estándar del noroeste: la industria
    // reporta toda su producción en "44 lb cartons" y el calibre de la pera
    // se define como cuántas caben en esa caja. Envuelta o no, el peso neto
    // de fruta es el mismo; wrapped describe el empaque, no el contenido.
    { match: '4/5 bushel cartons wrapped', lbs: 44 },
    { match: '4/5 bushel cartons', lbs: 44 },
    { match: 'cartons tray pack', lbs: 44 },
    // Media caja ("western standard half box" en el grade standard de USDA)
    { match: '2/5 bushel cartons wrapped', lbs: 22 },
    { match: '2/5 bushel cartons', lbs: 22 },
    { match: '1/2 cartons wrapped', lbs: 22 },
    { match: 'bushel cartons', lbs: 50 },
    { match: 'bushel', lbs: 50 },
  ],
  Blueberries: [
    { match: 'flats 12 1 pint cups', lbs: 11 },
    { match: 'flats 12 6 oz cups', lbs: 5 },
    { match: 'flats 12 4.4 oz cups', lbs: 4 },
    { match: 'flats 12 125 gram cups', lbs: 4 },
  ],
  Strawberries: [
    { match: 'flats 12 1 pint', lbs: 12 },
    { match: 'flats 8 1 lb containers', lbs: 8 },
  ],
  Lemons: [
    { match: '7/10 bushel cartons', lbs: 38 },
  ],
  Oranges: [
    { match: '4/5 bushel cartons', lbs: 43, origins: ['florida'] },
    { match: '7/10 bushel cartons', lbs: 42, origins: ['texas'] },
    { match: '7/10 bushel cartons', lbs: 38, origins: ['california'] },
  ],
  Kiwifruit: [
    { match: 'flats 1 layer', lbs: 7 },
  ],
  Mangoes: [
    { match: 'flats/cartons 1 layer', lbs: 10 },
    { match: 'flats 1 layer', lbs: 10 },
  ],
  Nectarines: [
    { match: 'cartons 2 layer tray pack', lbs: 18, origins: ['chile'] },
    { match: 'cartons 2 layer', lbs: 18, origins: ['chile'] },
    { match: 'cartons loose', lbs: 25 },
    { match: 'cartons 2 layer tray pack', lbs: 22 },
    { match: 'cartons 2 layer', lbs: 22 },
  ],
  Peaches: [
    { match: 'cartons 2 layer tray pack', lbs: 18, origins: ['chile'] },
    { match: 'cartons 2 layer', lbs: 18, origins: ['chile'] },
    { match: '1/2 bushel cartons', lbs: 25 },
    { match: '1/2 bushel baskets', lbs: 25 },
    { match: 'cartons loose', lbs: 25 },
    { match: 'cartons 2 layer tray pack', lbs: 22 },
    { match: 'cartons 2 layer', lbs: 22 },
  ],
  Plums: [
    { match: 'cartons 2 layer tray pack', lbs: 18, origins: ['chile'] },
    { match: 'cartons 2 layer', lbs: 18, origins: ['chile'] },
    { match: 'cartons loose', lbs: 28 },
    { match: 'cartons 2 layer tray pack', lbs: 22 },
    { match: 'cartons 2 layer', lbs: 22 },
  ],
};

/** Último recurso cuando el commodity no tiene tabla propia. */
const PACKAGE_WEIGHTS_FALLBACK: PackageWeightRule[] = [
  { match: 'cartons 2 layer', lbs: 25 },
  { match: 'cartons tray pack', lbs: 40 },
  { match: '4/5 bushel cartons', lbs: 44 },
  { match: '1/2 bushel cartons', lbs: 21 },
  { match: 'bushel cartons', lbs: 42 },
];

/**
 * Parses a USDA package description and returns total weight in lbs.
 * El commodity permite resolver formatos cuyo peso depende de la fruta.
 */
export const parsePackageLbs = (
  pkg: string,
  commodity?: string,
  origin?: string
): number | null => {
  if (!pkg) return null;
  const raw = pkg.toLowerCase().trim();
  // USDA alterna "2-layer", "2 layer" y "2layer" entre reportes.  La
  // normalización permite usar una sola regla sin alterar los pesos explícitos.
  const s = raw.replace(/-/g, ' ').replace(/\s+/g, ' ');
  const normalizedOrigin = origin?.toLowerCase() ?? '';

  const lbTotal = raw.match(/^(\d+(?:\.\d+)?)\s*lb/);
  if (lbTotal) return parseFloat(lbTotal[1]);

  const kgTotal = raw.match(/^(\d+(?:\.\d+)?)\s*kg/);
  if (kgTotal) return parseFloat(kgTotal[1]) * LB_PER_KG;

  // Formatos sin peso declarado: tabla de estándares, por commodity
  const table = commodity
    ? PACKAGE_WEIGHTS_BY_COMMODITY[commodity]
    : PACKAGE_WEIGHTS_FALLBACK;
  for (const rule of table ?? []) {
    const originMatches =
      !rule.origins ||
      rule.origins.some((expectedOrigin) => normalizedOrigin.includes(expectedOrigin));
    if (originMatches && s.includes(rule.match)) return rule.lbs;
  }

  const countLb = raw.match(/(\d+)\s+(\d+(?:\.\d+)?)-lb/);
  if (countLb) return parseInt(countLb[1]) * parseFloat(countLb[2]);

  const countOz = raw.match(/(\d+)\s+(\d+(?:\.\d+)?)-oz/);
  if (countOz) return (parseInt(countOz[1]) * parseFloat(countOz[2])) / 16;

  // Un peso en libras en cualquier parte del texto ("cartons 25 lb")
  const lbAnywhere = raw.match(/(\d+(?:\.\d+)?)\s*lb/);
  if (lbAnywhere) return parseFloat(lbAnywhere[1]);

  return null;
};

export const pricePerLb = (record: WholesaleRecord): number | null => {
  // El commodity sale de la fila, no del filtro: así el peso siempre
  // corresponde a la fruta de ese registro.
  const lbs = parsePackageLbs(record.package || '', record.commodity, record.origin);
  if (!lbs || lbs <= 0) return null;
  const avg = (parseFloat(record.low_price) + parseFloat(record.high_price)) / 2;
  if (isNaN(avg)) return null;
  return avg / lbs;
};

export const wholesalePriceInUnit = (record: WholesaleRecord, unit: UnitMode): number | null => {
  const plb = pricePerLb(record);
  if (plb === null) return null;
  return unit === 'lb' ? plb : plb * LB_PER_KG;
};

export const retailPriceInUnit = (record: RetailRecord, unit: UnitMode, commodity: string): number | null => {
  if (record.wtd_avg_price == null) return null;
  const unitLbs = RETAIL_UNIT_LBS[commodity] || 1.0;
  const perLb = record.wtd_avg_price / unitLbs;
  return unit === 'lb' ? perLb : perLb * LB_PER_KG;
};

export const getSeasonDates = (seasonName: string): { from: string; to: string } | null => {
  const season = Object.values(SEASONS).find((s) => s.name === seasonName);
  if (!season) return null;
  const fmt = (d: Date) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  return { from: fmt(season.start), to: fmt(season.end) };
};

export const fiveNumberSummary = (values: number[]): [number, number, number, number, number] | null => {
  if (values.length < 2) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const q1 = sorted[Math.floor(n * 0.25)];
  const median = sorted[Math.floor(n * 0.5)];
  const q3 = sorted[Math.floor(n * 0.75)];
  return [sorted[0], q1, median, q3, sorted[n - 1]];
};

// ============================================================
// API
// ============================================================

export const fetchReport = async (
  slugId: string,
  commodity: string,
  dateFrom: string,
  dateTo: string
): Promise<{ sections: Array<{ name: string; results: unknown[] }> }> => {
  // Goes through the usda-proxy edge function: it holds the USDA API key
  // server-side (the old hardcoded key + dev-only Vite proxy meant the
  // explorer leaked the key and couldn't work in production).
  //
  // Reports are cached in memory by (slug, commodity, range): USDA data is
  // historical, so re-searching, switching species back and forth, adding
  // one market or toggling VS temporada only hits the network for reports
  // not seen before. Caching the PROMISE also dedups in-flight requests.
  const q = commodity
    ? `report_begin_date=${dateFrom}:${dateTo};commodity=${commodity}`
    : `report_begin_date=${dateFrom}:${dateTo}`;
  const cacheKey = `${slugId}|${q}`;

  const cached = reportCache.get(cacheKey);
  if (cached) return cached;

  const promise = fetchReportUncached(slugId, q).then((res) => {
    // Don't poison the cache with transient failures
    if (res.sections.length === 0) reportCache.delete(cacheKey);
    return res;
  });
  if (reportCache.size >= REPORT_CACHE_MAX) {
    const oldest = reportCache.keys().next().value;
    if (oldest) reportCache.delete(oldest);
  }
  reportCache.set(cacheKey, promise);
  return promise;
};

const REPORT_CACHE_MAX = 300;
const reportCache = new Map<
  string,
  Promise<{ sections: Array<{ name: string; results: unknown[] }> }>
>();

const fetchReportUncached = async (
  slugId: string,
  q: string
): Promise<{ sections: Array<{ name: string; results: unknown[] }> }> => {
  const url =
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/usda-proxy` +
    `?slug=${encodeURIComponent(slugId)}&q=${encodeURIComponent(q)}&allSections=true`;

  // Retry transient failures (edge runtime "worker did not respond in
  // time" under concurrency, 429s, network blips). Without this a failed
  // report silently dropped a whole market from the chart.
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const resp = await fetch(url, {
        headers: {
          ...(await getEdgeFunctionHeaders()),
        },
      });
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data)) {
          return {
            sections: data.map((s: Record<string, unknown>) => ({
              name: (s.reportSection as string) || '',
              results: (s.results as unknown[]) || [],
            })),
          };
        }
        return {
          sections: [
            { name: 'default', results: (data as { results?: unknown[] }).results || [] },
          ],
        };
      }
      // Client errors (except 429) won't fix themselves — don't retry
      if (resp.status < 500 && resp.status !== 429) return { sections: [] };
    } catch {
      // network / JSON error — retry
    }
    await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
  }
  return { sections: [] };
};

// Fetch several reports with limited concurrency (sequential was painfully
// slow for the 17 movement reports — a full season took ~40s). The local
// edge runtime chokes above ~3 concurrent worker spawns ("worker did not
// respond in time"); hosted edge functions scale fine.
const CONCURRENCY = import.meta.env.DEV ? 3 : 6;

export const fetchMultipleReports = async (
  reports: Record<string, string>,
  commodity: string,
  dateFrom: string,
  dateTo: string,
  onProgress: (msg: string) => void
): Promise<WholesaleRecord[]> => {
  const all: WholesaleRecord[] = [];
  const entries = Object.entries(reports);
  let done = 0;

  for (let i = 0; i < entries.length; i += CONCURRENCY) {
    const batch = entries.slice(i, i + CONCURRENCY);
    await Promise.all(
      batch.map(async ([slugId, city]) => {
        try {
          const { sections } = await fetchReport(slugId, commodity, dateFrom, dateTo);
          const details = sections.find((s) => s.name === 'Report Details');
          if (details) all.push(...(details.results as WholesaleRecord[]));
        } catch {
          // skip
        } finally {
          done++;
          onProgress(`Fetching ${city}... (${done}/${entries.length})`);
        }
      })
    );
  }
  return all;
};
